/**
 * AddStayToGroupDialog — «Afegir una habitació al grup» des de la fitxa (runbook 7, F5 sobre B3).
 *
 * Només ADMIN i només per a una reserva **directa** (no OTA), **no cancel·lada** i amb número de
 * Beds24 numèric: un grup ha d'existir a Beds24. Obre un diàleg amb **una** `StayLineFields`
 * (dates i ocupació de la reserva de partida, preu buit) i el titular només informatiu. «Crear a
 * Beds24» → `useAddStayToGroup` (`POST /reservations/:id/stays`). En acabar bé, navega a la ruta
 * del grup enfocada a la nova (`buildGroupPathFocusedOn`); si el backend no torna el número de la
 * nova, fa servir el seu `group_path`. Si falla, toast i el diàleg es queda obert amb les dades.
 */

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StayLineFields } from '@/components/reservations';
import type { StaySibling } from '@/components/reservations/StayLineFields';
import {
  stayLineSchema,
  staysOverlap,
  type StayLineValues,
} from '@/lib/schemas/create-reservation';
import { isOtaChannel } from './EditStayDialog';
import { useAddStayToGroup, buildAddStayPayload, type AddedStay } from '@/hooks/useAddStayToGroup';
import { buildGroupPathFocusedOn, CALENDAR_PATH } from '@/lib/reservation-url';
import { calendarApi, api, type Booking, type Unit } from '@/services/api';

// ============================================
// HELPERS (purs, provats a AddStayToGroupDialog.test.tsx)
// ============================================

/** El mínim de la reserva de partida que fa servir el botó i el diàleg (surt de `ReservationCellData`). */
export interface AddStayStart {
  id: string;
  beds24Id?: string | null;
  guestName: string;
  channel?: string | null;
  status?: string | null;
  checkIn: string;
  checkOut: string;
  unitId?: string | null;
  numAdults?: number | null;
  numChildren?: number | null;
}

const NUMERIC_BEDS24_ID = /^\d+$/;

/** Directa, no cancel·lada i amb número de Beds24 numèric (els `manual-…` no poden fer grup). */
export function canAddStayToGroup(start: Pick<AddStayStart, 'channel' | 'status' | 'beds24Id'>): boolean {
  if (isOtaChannel(start.channel)) return false;
  if ((start.status ?? '').toUpperCase() === 'CANCELLED') return false;
  return NUMERIC_BEDS24_ID.test((start.beds24Id ?? '').trim());
}

const toDay = (value: string | null | undefined): string => (value ? String(value).slice(0, 10) : '');

/** Dates i ocupació de la reserva de partida; la unitat i el preu, a triar. */
export function addStayDefaults(start: AddStayStart): AddStayFormValues {
  const adults = start.numAdults ?? 0;
  const kids = start.numChildren ?? 0;
  return {
    stay: {
      unit_id: '',
      data_entrada: toDay(start.checkIn),
      data_sortida: toDay(start.checkOut),
      num_adults: Number.isFinite(adults) && adults >= 1 ? adults : 2,
      num_nens: Number.isFinite(kids) && kids >= 0 ? kids : 0,
      // NaN i no 0: el camp surt buit i el preu s'ha d'escriure a mà.
      preu_total: Number.NaN,
    },
    notes_internes: '',
  };
}

/**
 * Les estades del grup que ja coneixem: la de partida sempre i, de les germanes (`groupBeds24Ids`),
 * les que la graella ja té carregades a `['calendar-bookings']`. Sense repetits ni cancel·lades.
 */
export function groupSiblings(
  start: AddStayStart,
  groupBeds24Ids: readonly string[],
  loaded: ReadonlyArray<Pick<Booking, 'id' | 'beds24Id' | 'unitId' | 'checkIn' | 'checkOut' | 'status'>>,
): StaySibling[] {
  const siblings: StaySibling[] = [];
  const seen = new Set<string>();
  if (start.unitId) {
    siblings.push({
      unit_id: start.unitId,
      data_entrada: toDay(start.checkIn),
      data_sortida: toDay(start.checkOut),
      label: start.beds24Id ? `la reserva #${start.beds24Id}` : 'la reserva de partida',
    });
  }
  seen.add(start.id);
  const wanted = new Set(groupBeds24Ids);
  for (const b of loaded) {
    if (seen.has(b.id) || !wanted.has(b.beds24Id) || !b.unitId || b.status === 'CANCELLED') continue;
    seen.add(b.id);
    siblings.push({
      unit_id: b.unitId,
      data_entrada: toDay(b.checkIn),
      data_sortida: toDay(b.checkOut),
      label: `la reserva #${b.beds24Id}`,
    });
  }
  return siblings;
}

/** On anar després de crear: el grup enfocat a la nova; si no la sabem, el `group_path` del backend. */
export function pathAfterAdd(added: AddedStay): string {
  if (added.newBeds24Id && added.beds24Ids.length > 0) {
    const path = buildGroupPathFocusedOn(added.newBeds24Id, added.beds24Ids);
    if (path !== CALENDAR_PATH) return path;
  }
  return added.groupPath ?? CALENDAR_PATH;
}

// ============================================
// FORM
// ============================================

function addStaySchema(siblings: readonly StaySibling[]) {
  return z
    .object({
      stay: stayLineSchema,
      notes_internes: z.string().max(2000, 'Massa llarg'),
    })
    .superRefine((v, ctx) => {
      const clash = siblings.find((s) => staysOverlap(v.stay, s));
      if (clash) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Xoca amb ${clash.label} (mateixa unitat i dates)`,
          path: ['stay', 'unit_id'],
        });
      }
    });
}

/**
 * Quan valida el formulari (R4): els errors de camp (p. ex. «Tria una habitació») només després del
 * primer intent d'enviar i, a partir d'aquí, en viu. Obrir i tancar el selector no pinta res en
 * vermell. El xoc de `StayLineFields` no en depèn: surt de `useWatch` i es pinta en viu sempre.
 */
export const ADD_STAY_FORM_VALIDATION = { mode: 'onSubmit', reValidateMode: 'onChange' } as const;

export interface AddStayToGroupFormValues {
  stay: StayLineValues;
  notes_internes: string;
}
type AddStayFormValues = AddStayToGroupFormValues;

export interface AddStayToGroupFormProps {
  start: AddStayStart;
  units: readonly Unit[];
  propertyId?: number | null;
  siblings: readonly StaySibling[];
  isPending: boolean;
  onSubmit: (values: AddStayToGroupFormValues) => void;
  onCancel: () => void;
}

export function AddStayToGroupForm({
  start,
  units,
  propertyId,
  siblings,
  isPending,
  onSubmit,
  onCancel,
}: AddStayToGroupFormProps) {
  const schema = useMemo(() => addStaySchema(siblings), [siblings]);
  const form = useForm<AddStayToGroupFormValues>({
    resolver: zodResolver(schema),
    ...ADD_STAY_FORM_VALIDATION,
    defaultValues: addStayDefaults(start),
  });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">Titular</p>
        <p className="text-sm font-medium" data-testid="add-stay-holder">
          {start.guestName}
          {start.beds24Id && (
            <span className="ml-2 font-mono text-xs text-muted-foreground">#{start.beds24Id}</span>
          )}
        </p>
      </div>

      <StayLineFields
        control={form.control}
        prefix="stay"
        idPrefix="add-stay"
        units={units}
        propertyId={propertyId}
        siblings={siblings}
        disabled={isPending}
      />

      <div className="space-y-1.5">
        <Label htmlFor="add-stay-notes" className="text-xs text-gray-600">
          Notes internes (opcional)
        </Label>
        <Textarea id="add-stay-notes" rows={2} disabled={isPending} {...form.register('notes_internes')} />
      </div>

      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel·la
        </Button>
        <Button type="submit" disabled={isPending} aria-busy={isPending || undefined}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Crear a Beds24
        </Button>
      </DialogFooter>
    </form>
  );
}

// ============================================
// DIALOG
// ============================================

export interface AddStayToGroupDialogProps {
  start: AddStayStart;
  /** Números de Beds24 del grup que la fitxa ja coneix (`group_beds24_ids`). */
  groupBeds24Ids?: readonly string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PropertySource {
  beds24_property_id?: number | null;
}

export function AddStayToGroupDialog({
  start,
  groupBeds24Ids = [],
  open,
  onOpenChange,
}: AddStayToGroupDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Mateixa clau que la fitxa: ja és al cache.
  const { data: full, isLoading: loadingFull } = useQuery<PropertySource>({
    queryKey: ['reservation-full', start.id],
    queryFn: async () => {
      const res = await api.get(`/reservations/${start.id}`);
      return res.data;
    },
    enabled: open,
  });
  const propertyId = full?.beds24_property_id ?? null;
  const { data: units = [], isLoading: loadingUnits } = useQuery<Unit[]>({
    queryKey: ['calendar-units', propertyId],
    queryFn: () => calendarApi.getUnits(propertyId ?? undefined),
    enabled: open && !!propertyId,
    staleTime: 1000 * 60 * 60,
  });

  // Les germanes que la graella ja té carregades (cap crida nova).
  const siblings = useMemo(() => {
    if (!open) return [];
    const loaded = queryClient
      .getQueriesData<Booking[]>({ queryKey: ['calendar-bookings'] })
      .flatMap(([, data]) => (Array.isArray(data) ? data : []));
    return groupSiblings(start, groupBeds24Ids, loaded);
  }, [open, queryClient, start, groupBeds24Ids]);

  // `isPending` arriba un render tard: un doble tap ràpid podria crear dues habitacions.
  const inFlightRef = useRef(false);
  const mutation = useAddStayToGroup({
    onSuccess: (added) => {
      onOpenChange(false);
      const path = pathAfterAdd(added);
      if (path !== CALENDAR_PATH) navigate(path, { replace: true });
    },
  });

  const handleSubmit = (values: AddStayToGroupFormValues) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    mutation.mutate(
      { reservationId: start.id, payload: buildAddStayPayload(values.stay, values.notes_internes) },
      {
        onSettled: () => {
          inFlightRef.current = false;
        },
      },
    );
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && mutation.isPending) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* La fitxa és un Sheet no modal: sense el marcador, un clic dins del diàleg la tancaria. */}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-sheet-outside-safe="">
        <DialogHeader>
          <DialogTitle>Afegir una habitació al grup</DialogTitle>
          <DialogDescription>
            Es crea a Beds24 confirmada, al mateix grup i amb el mateix titular.
          </DialogDescription>
        </DialogHeader>
        {loadingFull || (propertyId && loadingUnits) ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <AddStayToGroupForm
            key={start.id}
            start={start}
            units={units}
            propertyId={propertyId}
            siblings={siblings}
            isPending={mutation.isPending}
            onSubmit={handleSubmit}
            onCancel={() => handleOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// BUTTON (el que pinta la fitxa)
// ============================================

export interface AddStayToGroupButtonProps {
  start: AddStayStart;
  groupBeds24Ids?: readonly string[];
}

/** Botó + diàleg. Només ADMIN i només si `canAddStayToGroup`; si no, no pinta res. */
export function AddStayToGroupButton({ start, groupBeds24Ids }: AddStayToGroupButtonProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  if (user?.role !== 'ADMIN' || !canAddStayToGroup(start)) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="w-full justify-start gap-2"
        onClick={() => setOpen(true)}
      >
        <Plus className="h-4 w-4" />
        Afegir una habitació al grup
      </Button>
      <AddStayToGroupDialog
        start={start}
        groupBeds24Ids={groupBeds24Ids}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
}
